import { supabase } from './supabase'

const MONDAY_API_URL = 'https://api.monday.com/v2/'

// ── In-memory fetch cache (survives tab switches, cleared on manual refresh) ──
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const _cache = new Map() // key → { data, ts }

function _cacheGet(key) {
    const entry = _cache.get(key)
    if (!entry) return null
    if (Date.now() - entry.ts > CACHE_TTL) { _cache.delete(key); return null }
    return entry.data
}

function _cacheSet(key, data) { _cache.set(key, { data, ts: Date.now() }) }

export function buildMondayCacheKey(prefix, session, ...extra) {
    const uid = session?.user?.id || 'anon'
    return `${prefix}:${uid}:${extra.join(':')}`
}

export function clearMondayCache(key) {
    if (key) _cache.delete(key)
    else _cache.clear()
}

// Execute GraphQL query/mutation — routed through Supabase proxy to bypass CORS
export async function mondayGraphQL(query, variables = {}, session = null) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const proxyUrl = `${supabaseUrl}/functions/v1/monday-proxy`

    const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, variables }),
    })

    const result = await response.json()
    
    if (result.errors) {
        throw new Error(result.errors[0].message || 'GraphQL error')
    }
    
    return result.data
}

// Fetch all boards with basic info
export async function fetchBoards(session) {
    const query = `
        query {
            boards(limit: 50) {
                id
                name
                description
                board_kind
                workspace {
                    id
                    name
                }
                groups {
                    id
                    title
                }
                columns {
                    id
                }
                items_count
            }
        }
    `
    
    const data = await mondayGraphQL(query, {}, session)
    // Add columns_count from columns array length
    return (data.boards || []).map(board => ({
        ...board,
        columns_count: board.columns?.length || 0
    }))
}

// Fetch board details with columns
export async function fetchBoardColumns(boardId, session) {
    const query = `
        query($boardId: [ID!]) {
            boards(ids: $boardId) {
                id
                name
                columns {
                    id
                    title
                    type
                    settings_str
                }
                groups {
                    id
                    title
                }
            }
        }
    `
    
    const data = await mondayGraphQL(query, { boardId: [boardId] }, session)
    return data.boards[0]
}

// Fetch items from a board with column values
export async function fetchBoardItems(boardId, limit = 100, cursor = null, session = null) {
    const query = `
        query($boardId: ID!, $limit: Int!, $cursor: String) {
            boards(ids: [$boardId]) {
                id
                name
                items_page(limit: $limit, cursor: $cursor) {
                    cursor
                    items {
                        id
                        name
                        created_at
                        updated_at
                        group {
                            id
                            title
                        }
                        column_values {
                            id
                            text
                            value
                            type
                        }
                        creator {
                            id
                            name
                            email
                        }
                    }
                }
            }
        }
    `
    
    const data = await mondayGraphQL(query, { boardId, limit, cursor }, session)
    return {
        items: data.boards[0]?.items_page?.items || [],
        cursor: data.boards[0]?.items_page?.cursor || null,
    }
}

// Fetch a single item's column values by id (board-agnostic)
export async function fetchItemById(itemId, session = null) {
    const data = await mondayGraphQL(
        `query($itemId: ID!) { items(ids: [$itemId]) { id column_values { id type text } } }`,
        { itemId: String(itemId) },
        session,
    )
    return data?.items?.[0] || null
}

// Create a new item on a board
export async function createItem(boardId, itemName, columnValues = {}, groupId = null, session = null) {
    const query = `
        mutation($boardId: ID!, $itemName: String!, $columnValues: JSON!, $groupId: String) {
            create_item(
                board_id: $boardId,
                item_name: $itemName,
                group_id: $groupId,
                column_values: $columnValues,
                create_labels_if_missing: true
            ) {
                id
                name
            }
        }
    `
    
    const variables = {
        boardId,
        itemName,
        columnValues: JSON.stringify(columnValues),
        groupId,
    }
    
    const data = await mondayGraphQL(query, variables, session)
    return data.create_item
}

// Update item name (uses change_simple_column_value with column_id "name")
export async function updateItemName(boardId, itemId, newName, session = null) {
    const query = `
        mutation($boardId: ID!, $itemId: ID!, $columnId: String!, $value: String!) {
            change_simple_column_value(
                board_id: $boardId,
                item_id: $itemId,
                column_id: $columnId,
                value: $value
            ) {
                id
                name
            }
        }
    `
    const data = await mondayGraphQL(query, { boardId, itemId, columnId: 'name', value: newName }, session)
    return data.change_simple_column_value
}

// Update a single column with a simple string value
export async function updateSimpleColumnValue(boardId, itemId, columnId, value, session = null) {
    const query = `
        mutation($boardId: ID!, $itemId: ID!, $columnId: String!, $value: String!) {
            change_simple_column_value(
                board_id: $boardId,
                item_id: $itemId,
                column_id: $columnId,
                value: $value
            ) {
                id
            }
        }
    `
    const data = await mondayGraphQL(query, { boardId, itemId, columnId, value }, session)
    return data.change_simple_column_value
}

// Update a single column with a JSON value (for location, etc.)
export async function updateColumnValue(boardId, itemId, columnId, value, session = null) {
    const query = `
        mutation($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
            change_column_value(
                board_id: $boardId,
                item_id: $itemId,
                column_id: $columnId,
                value: $value
            ) {
                id
            }
        }
    `
    const data = await mondayGraphQL(query, { boardId, itemId, columnId, value: JSON.stringify(value) }, session)
    return data.change_column_value
}

// Update column values for an item
export async function updateColumnValues(boardId, itemId, columnValues, session = null) {
    const query = `
        mutation($boardId: ID!, $itemId: ID!, $columnValues: JSON!) {
            change_multiple_column_values(
                board_id: $boardId,
                item_id: $itemId,
                column_values: $columnValues
            ) {
                id
                name
            }
        }
    `
    
    const data = await mondayGraphQL(query, { boardId, itemId, columnValues: JSON.stringify(columnValues) }, session)
    return data.change_multiple_column_values
}

// Helper to format column values for Monday.com API based on column type
export function formatColumnValue(type, value) {
    switch (type) {
        case 'text':
        case 'long_text':
            return { text: value }
        case 'numbers':
            return { number: value }
        case 'date':
            return { date: value } // Format: "YYYY-MM-DD"
        case 'status':
        case 'color':
            return { index: parseInt(value) || 0 }
        case 'dropdown':
            return { labels: Array.isArray(value) ? value : [value] }
        case 'email':
            return { email: value, text: value }
        case 'phone':
            return { phone: value, countryShortName: 'US' }
        case 'location':
            return { location: value }
        case 'link':
            return { url: value, text: value }
        case 'people':
            return { personsAndTeams: Array.isArray(value) ? value : [{ id: parseInt(value), kind: 'person' }] }
        case 'checkbox':
            return { checked: value === true || value === 'true' }
        case 'file':
            // Files need to be uploaded separately
            return null
        default:
            return { text: String(value) }
    }
}

// Fetch all project items from specified boards (for workflow integration)
export async function fetchAllProjects(boardIds, session = null, { force = false } = {}) {
    const cacheKey = buildMondayCacheKey('projects', session, boardIds.join(','))
    if (!force) {
        const cached = _cacheGet(cacheKey)
        if (cached) return cached
    }
    const allItems = []
    for (const boardId of boardIds) {
        let cursor = null
        let boardName = ''
        do {
            const query = `
                query($boardId: ID!, $cursor: String) {
                    boards(ids: [$boardId]) {
                        name
                        items_page(limit: 100, cursor: $cursor) {
                            cursor
                            items {
                                id
                                name
                                group { id title }
                                column_values { id text value type }
                            }
                        }
                    }
                }
            `
            const data = await mondayGraphQL(query, { boardId, cursor }, session)
            const board = data.boards[0]
            boardName = board?.name || boardId
            const page = board?.items_page
            if (page?.items) {
                for (const item of page.items) {
                    allItems.push({ item, boardId, boardName })
                }
            }
            cursor = page?.cursor || null
        } while (cursor)
    }
    _cacheSet(cacheKey, allItems)
    return allItems
}

// Fetch all items + groups from the Estimados 2026 architecture board
export async function fetchEstimatesBoard(session = null, { force = false } = {}) {
    const ESTIMATES_BOARD_ID = '18396590426'
    const cacheKey = buildMondayCacheKey('estimates', session)
    if (!force) {
        const cached = _cacheGet(cacheKey)
        if (cached) return cached
    }
    const query = `
        query($boardId: ID!, $cursor: String) {
            boards(ids: [$boardId]) {
                groups { id title }
                items_page(limit: 100, cursor: $cursor) {
                    cursor
                    items {
                        id
                        name
                        created_at
                        group { id title }
                        column_values { id text value type }
                    }
                }
            }
        }
    `
    const groups = []
    const items = []
    let cursor = null
    let groupsFetched = false
    do {
        const data = await mondayGraphQL(query, { boardId: ESTIMATES_BOARD_ID, cursor }, session)
        const board = data.boards[0]
        if (!groupsFetched && board?.groups) {
            groups.push(...board.groups)
            groupsFetched = true
        }
        const page = board?.items_page
        if (page?.items) items.push(...page.items)
        cursor = page?.cursor || null
    } while (cursor)
    const result = { groups, items }
    _cacheSet(cacheKey, result)
    return result
}

// Fetch project items filtered by client email
export async function fetchProjectsByEmail(boardIds, email, session = null, opts = {}) {
    const all = await fetchAllProjects(boardIds, session, opts)
    const normalizedEmail = (email || '').toLowerCase().trim()
    return all.filter(({ item }) => {
        const emailCol = item.column_values?.find(c => c.id === 'text_mkt9qv82')
        const itemEmail = (emailCol?.text || '').toLowerCase().trim()
        return itemEmail === normalizedEmail
    })
}

// Upload file to Monday.com
// Proxied through Supabase Edge Function because Monday's /v2/file lacks CORS headers
export async function uploadFile(boardId, itemId, columnId, file, session = null) {
    const query = `mutation ($file: File!) { add_file_to_column (item_id: ${itemId}, column_id: "${columnId}", file: $file) { id } }`

    const formData = new FormData()
    formData.append('query', query)
    formData.append('variables[file]', file)

    // Proxy through Supabase Edge Function to bypass CORS
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const proxyUrl = `${supabaseUrl}/functions/v1/monday-upload`

    const response = await fetch(proxyUrl, {
        method: 'POST',
        body: formData,
    })

    const result = await response.json()
    
    if (result.errors) {
        throw new Error(result.errors[0].message || 'File upload error')
    }
    
    return result.data?.add_file_to_column
}

// Fetch Monday user info (name, email, phone) by user IDs — cached
const userInfoCache = new Map()
export async function fetchUserInfo(userIds, session = null) {
    const missing = userIds.filter(id => id && !userInfoCache.has(id))
    if (missing.length > 0) {
        const query = `query($ids: [ID!]!) { users(ids: $ids) { id name email phone } }`
        try {
            const data = await mondayGraphQL(query, { ids: missing.map(String) }, session)
            for (const u of (data.users || [])) {
                userInfoCache.set(Number(u.id), { name: u.name, email: u.email, phone: u.phone })
            }
        } catch (err) {
            console.error('[monday] fetchUserInfo error:', err)
        }
    }
    const result = {}
    for (const id of userIds) {
        if (id && userInfoCache.has(id)) result[id] = userInfoCache.get(id)
    }
    return result
}

// Fetch the latest N updates (comments) from a Monday item
export async function fetchItemUpdates(itemId, limit = 10, session = null) {
    const query = `
        query($itemId: [ID!]!, $limit: Int!) {
            items(ids: $itemId) {
                updates(limit: $limit) {
                    id
                    body
                    created_at
                    creator {
                        id
                        name
                    }
                }
            }
        }
    `
    const data = await mondayGraphQL(query, { itemId: [String(itemId)], limit }, session)
    return data.items?.[0]?.updates || []
}

// Post a comment (update) on a Monday item
export async function postMondayComment(itemId, text, session = null) {
    const query = `
        mutation($itemId: ID!, $body: String!) {
            create_update(item_id: $itemId, body: $body) {
                id
            }
        }
    `
    const data = await mondayGraphQL(query, { itemId: String(itemId), body: text }, session)
    return data.create_update
}

// Fetch a single item's created_at timestamp (lightweight, no columns)
export async function fetchItemCreatedAt(itemId, session = null) {
    const query = `
        query($itemId: [ID!]!) {
            items(ids: $itemId) {
                created_at
            }
        }
    `
    const data = await mondayGraphQL(query, { itemId: [String(itemId)] }, session)
    return data.items?.[0]?.created_at || null
}

